import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, ScanLine, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Modal, inputCls, saveDoc, useList } from "@/components/kit";
import { api, errMsg, fmtMoney } from "@/lib/api";

const TYPES = ["Matrimonio", "Cerimonia", "Compleanno", "Aziendale", "Altro"];

function PresetForm({ preset, fields, onDone }) {
  const [f, setF] = useState({ nome: "", evento_tipi: [], formula: "", prezzo_adulti: "", prezzo_bambini: "", descrizione: "", portate: {}, attivo: true, ...preset });
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const read = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/menu-presets/read", fd);
      setF((x) => ({ ...x, formula: r.data.formula || x.formula, portate: { ...x.portate, ...r.data.portate } }));
      toast.success(`Menù letto: ${Object.keys(r.data.portate).length} portate compilate. Controlla e salva.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };
  const save = async () => {
    const { id, created_at, updated_at, created_by, ...body } = f;
    if (await saveDoc("/menu-presets", id, { ...body, prezzo_adulti: f.prezzo_adulti === "" ? "" : Number(f.prezzo_adulti), prezzo_bambini: f.prezzo_bambini === "" ? "" : Number(f.prezzo_bambini) })) onDone();
  };
  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-[#EAF5F7] p-4 flex flex-wrap items-center gap-3">
        <input ref={ref} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={(e) => read(e.target.files[0])} data-testid="preset-file-input" />
        <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => ref.current.click()} data-testid="preset-read-button">{busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ScanLine size={14} className="mr-2" />} {busy ? "Sto leggendo il menù…" : "Fotografa o carica un menù (foto/PDF)"}</Button>
        <span className="text-xs text-[#47606B]">L'IA compila le portate da sola.</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nome del menù *" wide><input className={inputCls} value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} placeholder="es. Menù Gold di mare" data-testid="preset-name" /></Field>
        <Field label="Formula"><input className={inputCls} value={f.formula} onChange={(e) => setF({ ...f, formula: e.target.value })} placeholder="es. Servito al tavolo" data-testid="preset-formula" /></Field>
        <Field label="Prezzo adulti (€/persona)"><input type="number" className={inputCls} value={f.prezzo_adulti} onChange={(e) => setF({ ...f, prezzo_adulti: e.target.value })} data-testid="preset-price-adults" /></Field>
        <Field label="Prezzo bambini (€/persona)"><input type="number" className={inputCls} value={f.prezzo_bambini} onChange={(e) => setF({ ...f, prezzo_bambini: e.target.value })} data-testid="preset-price-kids" /></Field>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        {TYPES.map((t) => <button key={t} onClick={() => setF({ ...f, evento_tipi: f.evento_tipi.includes(t) ? f.evento_tipi.filter((x) => x !== t) : [...f.evento_tipi, t] })} data-testid={`preset-event-${t}`} className={`px-3 py-1.5 rounded-full border ${f.evento_tipi.includes(t) ? "bg-[#0B6E8F] text-white border-[#0B6E8F]" : "border-[#D4E6EC]"}`}>{t}</button>)}
        <span className="text-[#7B929C] self-center">(nessuno = tutti gli eventi)</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {fields.map((x) => <Field key={x.k} label={x.label}><textarea rows={3} className={inputCls} value={f.portate[x.k] || ""} onChange={(e) => setF({ ...f, portate: { ...f.portate, [x.k]: e.target.value } })} data-testid={`preset-field-${x.k}`} /></Field>)}
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.attivo !== false} onChange={(e) => setF({ ...f, attivo: e.target.checked })} data-testid="preset-active" /> Attivo</label>
      <Button className="rounded-full" onClick={save} disabled={!f.nome.trim()} data-testid="preset-save">Salva menù</Button>
    </div>
  );
}

export function MenuPresets() {
  const [rows, reload] = useList("/menu-presets");
  const [fields, setFields] = useState([]);
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    api.get("/menu-presets/fields").then((r) => setFields(r.data));
  }, []);
  const label = (k) => fields.find((x) => x.k === k)?.label || k;
  const remove = async () => {
    await api.delete(`/menu-presets/${edit.id}`);
    setEdit(null);
    reload();
  };
  return (
    <div className="space-y-4" data-testid="menu-presets">
      <div className="flex flex-wrap justify-between gap-3 items-center">
        <p className="text-sm text-[#47606B] max-w-2xl">Menù completi pronti da caricare con un tocco nei preventivi e nell'Allegato A del contratto. Potete scriverli o fotografare un vostro menù.</p>
        <Button className="rounded-full" onClick={() => setEdit({})} data-testid="new-preset-button"><Plus size={15} className="mr-2" /> Nuovo menù</Button>
      </div>
      {rows && rows.length === 0 && <div className="text-sm text-[#7B929C] py-6" data-testid="presets-empty">Nessun menù preimpostato ancora.</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(rows || []).map((p, i) => (
          <button key={p.id} onClick={() => setEdit(p)} className={`text-left rounded-xl border border-[#D4E6EC] bg-white p-5 hover:border-[#0B6E8F] hover:-translate-y-0.5 transition-[transform,border-color] fade-up ${p.attivo === false ? "opacity-50" : ""}`} style={{ animationDelay: `${i * 40}ms` }} data-testid={`preset-card-${p.id}`}>
            <div className="flex justify-between gap-2"><span className="font-serif-it text-xl font-semibold">{p.nome}</span>{p.prezzo_adulti !== "" && p.prezzo_adulti != null && <span className="text-[#0B6E8F] font-semibold whitespace-nowrap">{fmtMoney(p.prezzo_adulti)}</span>}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#34B3B0] mt-1">{p.formula}{(p.evento_tipi || []).length ? ` • ${p.evento_tipi.join(", ")}` : ""}</div>
            <div className="text-xs text-[#47606B] mt-3 space-y-1">{Object.entries(p.portate || {}).slice(0, 4).map(([k, v]) => <div key={k} className="line-clamp-1"><b>{label(k)}:</b> {v}</div>)}</div>
          </button>
        ))}
      </div>
      <Modal open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? edit.nome : "Nuovo menù preimpostato"} testId="preset-dialog">
        {edit && <PresetForm preset={edit} fields={fields} onDone={() => { setEdit(null); reload(); }} />}
        {edit?.id && <Button variant="ghost" className="rounded-full text-[#991B1B]" onClick={remove} data-testid="preset-delete"><Trash2 size={14} className="mr-1" /> Elimina</Button>}
      </Modal>
    </div>
  );
}
